import { useState } from "react";

import { Button } from "../base/Button";
import {
  IInitialPriceEstimateRequest,
  MAX_AI_PROMPT_LENGTH,
} from "../../models/Category/InitialPriceEstimate";

interface AiPromptSettingsProps {
  config: IInitialPriceEstimateRequest;
  /** What a blank prompt inherits, from the API. */
  defaultAiPrompt: string;
  onChange: (patch: Partial<IInitialPriceEstimateRequest>) => void;
}

/**
 * This category's AI match prompt. The lookup asks the model which listings really are this part
 * for the donor vehicle, and this is what it is told. Blank inherits the default from the AI prompt
 * modal; the default is shown here so an admin can see what they are overriding and start from it.
 */
export default function AiPromptSettings({
  config,
  defaultAiPrompt,
  onChange,
}: AiPromptSettingsProps) {
  const [showDefault, setShowDefault] = useState(false);

  const prompt = config.aiPrompt ?? "";
  const usingDefault = prompt.trim().length === 0;

  return (
    <section className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
          AI Match Check
        </h2>
        {usingDefault ? (
          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
            Using the default prompt
          </span>
        ) : (
          <span className="rounded bg-orange-100 px-2 py-0.5 text-xs text-orange-800">
            Custom prompt for this category
          </span>
        )}
      </div>

      <label className="flex flex-col space-y-1">
        <span className="text-sm font-medium text-black opacity-80">
          Prompt for this category (optional)
        </span>
        <textarea
          value={prompt}
          rows={10}
          maxLength={MAX_AI_PROMPT_LENGTH}
          placeholder="Leave blank to use the default prompt"
          onChange={(event) => onChange({ aiPrompt: event.target.value })}
          className="w-full rounded border border-orange-600 px-3 py-2 font-mono text-sm outline-none focus:ring-2 focus:ring-orange-600"
        />
      </label>
      <p className="mt-1 text-xs text-gray-500">
        Tells the AI how to decide whether an eBay listing is this part for the
        donor vehicle. The vehicle, the search and the listing titles are added
        for every check, so write the rules, not the facts. Saves with the
        category and is live on the next lookup; no category sync needed.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          variant="outlined"
          size="sm"
          onClick={() => setShowDefault((value) => !value)}
        >
          {showDefault ? "Hide default prompt" : "Show default prompt"}
        </Button>
        {usingDefault ? (
          <Button
            variant="outlined"
            size="sm"
            onClick={() => onChange({ aiPrompt: defaultAiPrompt })}
          >
            Start from the default
          </Button>
        ) : (
          <Button
            variant="outlined"
            size="sm"
            onClick={() => onChange({ aiPrompt: "" })}
          >
            Back to the default
          </Button>
        )}
      </div>

      {showDefault && (
        <pre className="mt-3 max-h-72 overflow-y-auto whitespace-pre-wrap rounded bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700">
          {defaultAiPrompt}
        </pre>
      )}
    </section>
  );
}
